import { useEffect, useRef } from 'react';

import { readScreenLook, resolvedLook } from '../lib/screenLook';

// Cloudflare Turnstile, explicit-render integration (threat E1). The script
// is loaded once and lazily — only when a site key exists, so local dev /
// test (no key) never touches Cloudflare. We drive the widget through
// window.turnstile.render() so React owns its lifecycle.
//
// Tokens are single-use: after a rejected submit the host bumps
// `resetSignal` to fetch a fresh token for the retry. If the script fails to
// load (blocker / offline / CDN outage) the host is notified via `onError`
// so it can show a retryable message instead of a silently dead form.

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface RenderOptions {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  appearance?: 'always' | 'execute' | 'interaction-only';
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  'timeout-callback'?: () => void;
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: RenderOptions) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      // Remove the dead tag and clear the cached promise so a later mount
      // (e.g. the user's retry) re-attempts a clean load instead of leaving
      // orphaned <script> elements accumulating in <head>.
      script.remove();
      scriptPromise = null;
      reject(new Error('Failed to load Turnstile'));
    });
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface TurnstileWidgetProps {
  siteKey: string;
  onToken: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  resetSignal?: number;
  /** A fixed widget theme for a page drawn in one palette (TOM-980). Unset follows the screen look. */
  theme?: 'light' | 'dark';
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onExpire,
  onError,
  resetSignal = 0,
  theme,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Keep the latest callbacks in refs so the render effect can depend on
  // siteKey alone (render once) without going stale.
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          // The widget iframe can't be restyled, so make it unobtrusive
          // instead: `interaction-only` keeps it invisible for the ~99% of
          // legitimate users who pass silently — it only appears when
          // Cloudflare genuinely needs a challenge — and the token still fires
          // invisibly for clean users (the submit gating waits for it).
          // `flexible` spans the form width on the rare time it shows. The
          // theme is the host's fixed one when it sets one (the home page's
          // dark form, TOM-980); otherwise it follows the screen look at
          // render time (Turnstile has no night theme, so Night uses dark).
          // (ADR 0065)
          theme: theme ?? (resolvedLook(readScreenLook()) === 'light' ? 'light' : 'dark'),
          appearance: 'interaction-only',
          size: 'flexible',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onExpireRef.current?.(),
          'error-callback': () => onExpireRef.current?.(),
          'timeout-callback': () => onExpireRef.current?.(),
        });
      })
      .catch(() => {
        // Script blocked (offline, content blocker, CDN outage). Notify the
        // host so it can render a retryable error rather than an
        // uncompletable form with no explanation.
        if (!cancelled) onErrorRef.current?.();
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme]);

  useEffect(() => {
    if (resetSignal > 0 && widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [resetSignal]);

  return <div ref={containerRef} className="turnstile-widget" data-testid="turnstile-widget" />;
}
